/**
 * Ped: humanoid NPC with civilian AI (wander on the walk graph, idle, flee), ragdoll-lite
 * knockdowns, vehicle seating and city/vehicle collision. Cops (and any `controlled` ped) are
 * driven externally through moveToward/faceToward; this class still owns their animation,
 * collision and transform.
 *
 * Extra public members beyond IPed (documented in the final report):
 *   releaseOnExit  - set by TrafficSystem on its drivers: exitVehicle() hands the ped back to the civilian AI
 *   wantsOut       - a seated civilian that got hurt wants the car stopped so it can bail out (read by TrafficSystem)
 *   seat           - seat index while seated
 */
import * as THREE from 'three';
import { G } from './ctx';
import { Humanoid, randomStyle } from './humanoid';
import { clamp, damp, dampAngle, angleDiff, TAU } from './util';
import { chooseNext, laneIndex, projDist2, projectT, reverseLane } from './npc_lanes';
import type { DamageSource, HumanoidAnim, HumanoidStyle, IHumanoid, IPed, IVehicle, PathLane, PedRole, PedState } from './types';

let nextId = 1;

const RADIUS = 0.35;
const FLEE_SPEED = 5.5;
const CTRL_WALK = 2.4;
const CTRL_RUN = 6.0;
const LIE_TIME = 2.5;

// shared temporaries (never allocate in update)
const _a = new THREE.Vector3();

export class Ped implements IPed {
  readonly id = nextId++;
  group = new THREE.Group();
  humanoid: IHumanoid;
  pos = new THREE.Vector3();
  heading = 0;
  health = 100;
  state: PedState = 'idle';
  controlled = false;
  vehicle: IVehicle | null = null;
  persistent = false;
  deadSince = 0;
  speed = 0;

  // ---- extras (public, not in the contract)
  releaseOnExit = false;
  wantsOut = false;
  seat = 0;

  // ---- private AI state
  private laneIdx = -1;
  private nextLaneIdx = -1;
  private readonly walkSpeed: number;
  private readonly phone: boolean;
  private readonly wobble: number;
  private idleUntil = 0;
  private idleBase = 0;
  private lookTarget = 0;
  private lookNext = 0;
  private nextStopCheck = 0;
  private wanderUntil = 0;
  private fleeX = 0;
  private fleeZ = 0;
  private fleeUntil = 0;
  private fleeBias = 0;
  private fleeBiasUntil = 0;
  private blockedT = 0;
  private knockUntil = 0;
  private knockFromX = 0;
  private knockFromZ = 0;
  private slideX = 0;
  private slideZ = 0;
  private spin = 0;
  private hitUntil = 0;
  private shootUntil = 0;
  private mvActive = false;
  private mvSpeed = 0;
  private pendingExit = false;
  private touchedWall = false;
  /** hip height of the humanoid (the seat position is where the hips go) */
  private readonly hipH: number;

  constructor(
    public role: PedRole,
    x: number,
    z: number,
    style?: Partial<HumanoidStyle>,
  ) {
    const rng = G.rng;
    let base: Partial<HumanoidStyle> | undefined = style;
    if (role === 'cop') base = { uniform: 'police', hat: 'police', shirt: 0x1a2a5a, pants: 0x101830, ...style };
    else if (role === 'medic') base = { uniform: 'medic', shirt: 0xf0f0f0, pants: 0xe8e8ee, ...style };
    const hum = new Humanoid(randomStyle(rng, base));
    this.humanoid = hum;
    this.hipH = typeof hum.hipHeight === 'number' && Number.isFinite(hum.hipHeight) ? hum.hipHeight : 0.9;
    this.group.add(this.humanoid.group);
    this.group.userData.entity = this;
    this.group.userData.kind = 'ped';
    this.pos.set(x, 0, z);
    this.heading = rng() * TAU;
    this.group.position.copy(this.pos);
    this.group.rotation.y = this.heading;
    this.walkSpeed = 1.2 + rng() * 0.6;
    this.phone = rng() < 0.12;
    this.wobble = rng() * TAU;
    // start with a short idle so a fresh crowd does not all step off at once
    this.idleUntil = G.time.t + rng() * 1.5;
    this.idleBase = this.heading;
    this.nextStopCheck = G.time.t + 2;
  }

  // ================================================================== update ====

  update(dt: number): void {
    const t = G.time.t;
    if (this.vehicle) {
      this.updateSeated(dt);
      return;
    }
    if (this.state === 'dead') {
      this.updateDead(dt);
      return;
    }
    if (this.state === 'knocked') {
      this.updateKnocked(dt, t);
      return;
    }
    // alive and on foot: cars first (a hit changes the state for this frame)
    this.checkVehicles(t);
    const st = this.state as PedState;
    if (st === 'knocked' || st === 'dead') {
      this.finish(dt, t);
      return;
    }
    if (this.controlled) this.updateControlled(dt, t);
    else this.updateCivilian(dt, t);
    this.finish(dt, t);
  }

  // ---- seated -----------------------------------------------------------------

  private updateSeated(dt: number): void {
    const v = this.vehicle!;
    if (this.pendingExit || this.state === 'dead') {
      // died inside: the body ends up next to the door
      this.exitVehicle();
      this.updateDead(dt);
      return;
    }
    this.syncSeat(v);
    this.speed = 0;
    this.setAnim(v.driver === this ? 'drive' : 'sit');
    this.humanoid.update(dt, 0);
  }

  /** pos = seat at ground level; the humanoid root carries the seat height (hips on the seat) */
  private syncSeat(v: IVehicle): void {
    v.seatPosition(this.seat, _a);
    this.pos.set(_a.x, 0, _a.z);
    this.heading = v.heading;
    this.group.position.copy(this.pos);
    this.group.rotation.y = this.heading;
    this.humanoid.group.position.y = _a.y - this.hipH;
  }

  // ---- dead / knocked ----------------------------------------------------------

  private integrateSlide(dt: number): void {
    if (this.slideX !== 0 || this.slideZ !== 0) {
      this.pos.x += this.slideX * dt;
      this.pos.z += this.slideZ * dt;
      const k = Math.exp(-4 * dt);
      this.slideX *= k;
      this.slideZ *= k;
      this.heading += this.spin * dt;
      this.spin *= k;
      if (Math.abs(this.slideX) + Math.abs(this.slideZ) < 0.02) {
        this.slideX = 0;
        this.slideZ = 0;
        this.spin = 0;
      }
      const hit = G.city.resolveCircle(this.pos.x, this.pos.z, RADIUS);
      if (hit) {
        this.pos.x = hit.x;
        this.pos.z = hit.z;
        this.slideX *= 0.3;
        this.slideZ *= 0.3;
      }
      G.city.clampToBounds(this.pos);
    }
  }

  private updateDead(dt: number): void {
    this.speed = 0;
    this.integrateSlide(dt);
    this.pos.y = 0;
    this.group.position.copy(this.pos);
    this.group.rotation.y = this.heading;
    this.setAnim('dead');
    this.humanoid.update(dt, 0);
  }

  private updateKnocked(dt: number, t: number): void {
    this.speed = 0;
    this.integrateSlide(dt);
    if (t >= this.knockUntil) {
      // get up
      this.state = this.controlled ? 'controlled' : 'idle';
      this.hitUntil = t + 0.6;
      this.slideX = this.slideZ = this.spin = 0;
      this.idleUntil = t + 0.6;
      this.idleBase = this.heading;
      if (!this.controlled && this.role !== 'cop') this.scare(this.knockFromX, this.knockFromZ);
    }
    this.pos.y = 0;
    this.group.position.copy(this.pos);
    this.group.rotation.y = this.heading;
    this.setAnim(this.state === 'knocked' ? 'dead' : 'hit');
    this.humanoid.update(dt, 0);
  }

  // ---- vehicles ----------------------------------------------------------------

  /** run over by a moving car? scared by a car heading at us? */
  private checkVehicles(t: number): void {
    const vehicles = G.entities.vehicles;
    const px = this.pos.x, pz = this.pos.z;
    for (let i = 0; i < vehicles.length; i++) {
      const v = vehicles[i];
      if (v.driver === this) continue;
      const dx = px - v.pos.x, dz = pz - v.pos.z;
      const d2 = dx * dx + dz * dz;
      if (d2 > 64) continue;
      const sp = Math.abs(v.speed);
      if (sp > 1.5 && v.containsPoint(px, pz, 0.3)) {
        const vx = v.velocity.x, vz = v.velocity.z;
        const vl = Math.hypot(vx, vz);
        let nx: number, nz: number;
        if (vl > 0.05) {
          nx = vx / vl;
          nz = vz / vl;
        } else {
          nx = Math.sin(v.heading) * Math.sign(v.speed || 1);
          nz = Math.cos(v.heading) * Math.sign(v.speed || 1);
        }
        const src: DamageSource = v.driver === 'player' ? 'player' : 'vehicle';
        this.knockDown(nx, nz, sp * 4, src);
        if (v.driver === 'player') G.events.emit('crime', { kind: 'ped_hit', x: px, z: pz });
        return;
      }
      // a fast car closing in: get out of the way
      if (!this.controlled && this.state !== 'flee' && sp > 6 && d2 < 36) {
        const d = Math.sqrt(d2) || 1;
        const closing = (v.velocity.x * dx + v.velocity.z * dz) / d;
        if (closing > 4) {
          this.scare(v.pos.x, v.pos.z);
          return;
        }
      }
    }
    void t;
  }

  /** push out of stationary cars (moving ones knock us down instead) */
  private pushOutOfVehicles(): void {
    const vehicles = G.entities.vehicles;
    const px = this.pos.x, pz = this.pos.z;
    for (let i = 0; i < vehicles.length; i++) {
      const v = vehicles[i];
      if (v.driver === this) continue;
      const dx = px - v.pos.x, dz = pz - v.pos.z;
      if (dx * dx + dz * dz > 64) continue;
      if (!v.containsPoint(px, pz, RADIUS)) continue;
      const s = Math.sin(v.heading), c = Math.cos(v.heading);
      const fwd = dx * s + dz * c;
      const side = dx * c - dz * s;
      const pf = v.spec.length / 2 + RADIUS - Math.abs(fwd);
      const ps = v.spec.width / 2 + RADIUS - Math.abs(side);
      if (ps < pf) {
        const sg = side >= 0 ? 1 : -1;
        this.pos.x += c * ps * sg;
        this.pos.z -= s * ps * sg;
      } else {
        const sg = fwd >= 0 ? 1 : -1;
        this.pos.x += s * pf * sg;
        this.pos.z += c * pf * sg;
      }
      this.touchedWall = true;
    }
  }

  // ---- controlled --------------------------------------------------------------

  private updateControlled(dt: number, t: number): void {
    if (this.state !== 'controlled') this.state = 'controlled';
    if (this.mvActive && t >= this.hitUntil) {
      this.speed = this.mvSpeed;
      this.pos.x += Math.sin(this.heading) * this.speed * dt;
      this.pos.z += Math.cos(this.heading) * this.speed * dt;
    } else {
      this.speed = damp(this.speed, 0, 14, dt);
      if (this.speed < 0.05) this.speed = 0;
      this.mvSpeed = 0;
    }
    this.mvActive = false;
  }

  // ---- civilian AI --------------------------------------------------------------

  private updateCivilian(dt: number, t: number): void {
    if (t < this.hitUntil) {
      // staggering: no locomotion
      this.speed = 0;
      return;
    }
    switch (this.state) {
      case 'flee':
        if (t >= this.fleeUntil) {
          this.state = 'walk';
          this.laneIdx = -1;
          this.fleeBias = 0;
        } else {
          this.flee(dt, t);
          return;
        }
        break;
      case 'controlled':
      case 'seated':
        this.state = 'idle';
        this.idleUntil = t;
        break;
      default:
        break;
    }
    if (this.state === 'idle') {
      if (t >= this.idleUntil) {
        this.state = 'walk';
        this.nextStopCheck = t + 4;
      } else {
        this.idle(dt, t);
        return;
      }
    }
    this.walk(dt, t);
  }

  private idle(dt: number, t: number): void {
    this.speed = damp(this.speed, 0, 10, dt);
    if (this.speed < 0.05) this.speed = 0;
    if (t >= this.lookNext) {
      this.lookNext = t + 1.2 + G.rng() * 2.2;
      this.lookTarget = this.idleBase + (G.rng() - 0.5) * (this.phone ? 0.5 : 1.6);
    }
    this.heading = dampAngle(this.heading, this.lookTarget, 2.5, dt);
  }

  private startIdle(t: number): void {
    this.state = 'idle';
    this.idleUntil = t + (this.phone ? 8 + G.rng() * 7 : 2 + G.rng() * 4);
    this.idleBase = this.heading + (G.rng() - 0.5) * 1.2;
    this.lookTarget = this.idleBase;
    this.lookNext = t + 1 + G.rng();
    this.nextStopCheck = this.idleUntil + 6;
  }

  private walk(dt: number, t: number): void {
    const g = G.city.walkGraph;
    const lanes = g.lanes;
    if (lanes.length === 0) {
      this.freeWander(dt, t);
      return;
    }
    if (this.laneIdx < 0 || this.laneIdx >= lanes.length) {
      const n = g.nearest(this.pos.x, this.pos.z);
      const idx = laneIndex(g, n.id);
      if (idx < 0) {
        this.freeWander(dt, t);
        return;
      }
      this.laneIdx = idx;
      this.nextLaneIdx = this.pickNext(idx);
    }
    let lane: PathLane = lanes[this.laneIdx];
    let tp = projectT(lane, this.pos.x, this.pos.z);
    if (projDist2 > 36) {
      // pushed far off the path (flee, crowd): re-acquire
      this.laneIdx = -1;
      this.speed = damp(this.speed, 0, 6, dt);
      return;
    }
    const endDx = lane.b.x - this.pos.x, endDz = lane.b.z - this.pos.z;
    if (tp >= 0.995 || endDx * endDx + endDz * endDz < 0.36) {
      if (this.nextLaneIdx < 0) {
        this.laneIdx = -1;
        this.startIdle(t);
        return;
      }
      this.laneIdx = this.nextLaneIdx;
      this.nextLaneIdx = this.pickNext(this.laneIdx);
      lane = lanes[this.laneIdx];
      tp = projectT(lane, this.pos.x, this.pos.z);
    }
    // look-ahead point (may run into the next lane for a smooth corner)
    const len = lane.length > 0.01 ? lane.length : 0.01;
    const s = tp * len + 1.4;
    let tx: number, tz: number;
    if (s <= len) {
      const k = s / len;
      tx = lane.a.x + (lane.b.x - lane.a.x) * k;
      tz = lane.a.z + (lane.b.z - lane.a.z) * k;
    } else if (this.nextLaneIdx >= 0) {
      const nl = lanes[this.nextLaneIdx];
      const nlen = nl.length > 0.01 ? nl.length : 0.01;
      const k = clamp((s - len) / nlen, 0, 1);
      tx = nl.a.x + (nl.b.x - nl.a.x) * k;
      tz = nl.a.z + (nl.b.z - nl.a.z) * k;
    } else {
      tx = lane.b.x;
      tz = lane.b.z;
    }
    const desired = Math.atan2(tx - this.pos.x, tz - this.pos.z);
    this.heading = dampAngle(this.heading, desired, 6, dt);
    const align = clamp(Math.cos(angleDiff(this.heading, desired)) + 0.35, 0.25, 1);
    this.speed = damp(this.speed, this.walkSpeed * align, 5, dt);
    this.pos.x += Math.sin(this.heading) * this.speed * dt;
    this.pos.z += Math.cos(this.heading) * this.speed * dt;

    // occasional stop (look around / phone)
    if (t >= this.nextStopCheck) {
      this.nextStopCheck = t + 1;
      if (G.rng() < 0.03) this.startIdle(t);
    }
  }

  private pickNext(idx: number): number {
    const g = G.city.walkGraph;
    let n = chooseNext(g, idx, G.rng, 0.7);
    if (n < 0) n = reverseLane(g, idx);
    return n;
  }

  /** no walk graph available (stub city): stroll in straight lines, turn on walls */
  private freeWander(dt: number, t: number): void {
    if (t >= this.wanderUntil) {
      this.wanderUntil = t + 3 + G.rng() * 5;
      this.lookTarget = this.heading + (G.rng() - 0.5) * 2.5;
    }
    if (this.touchedWall) {
      this.lookTarget = this.heading + Math.PI * (0.5 + G.rng() * 0.5) * (G.rng() < 0.5 ? -1 : 1);
      this.wanderUntil = t + 2 + G.rng() * 3;
    }
    this.heading = dampAngle(this.heading, this.lookTarget, 4, dt);
    this.speed = damp(this.speed, this.walkSpeed, 5, dt);
    this.pos.x += Math.sin(this.heading) * this.speed * dt;
    this.pos.z += Math.cos(this.heading) * this.speed * dt;
    if (t >= this.nextStopCheck) {
      this.nextStopCheck = t + 1;
      if (G.rng() < 0.03) this.startIdle(t);
    }
  }

  private flee(dt: number, t: number): void {
    let ax = this.pos.x - this.fleeX, az = this.pos.z - this.fleeZ;
    const d = Math.hypot(ax, az);
    if (d < 0.05) {
      ax = Math.sin(this.heading);
      az = Math.cos(this.heading);
    }
    if (t >= this.fleeBiasUntil) this.fleeBias = 0;
    if (this.touchedWall) {
      this.blockedT += dt;
      if (this.blockedT > 0.3) {
        this.blockedT = 0;
        this.fleeBias = (G.rng() < 0.5 ? -1 : 1) * (Math.PI * 0.5 + G.rng() * 0.6);
        this.fleeBiasUntil = t + 1.2 + G.rng();
      }
    } else if (this.blockedT > 0) this.blockedT = Math.max(0, this.blockedT - dt * 0.5);
    const ang = Math.atan2(ax, az) + this.fleeBias + Math.sin(t * 2.3 + this.wobble) * 0.22;
    this.heading = dampAngle(this.heading, ang, 7, dt);
    this.speed = damp(this.speed, FLEE_SPEED, 6, dt);
    this.pos.x += Math.sin(this.heading) * this.speed * dt;
    this.pos.z += Math.cos(this.heading) * this.speed * dt;
  }

  /** light separation from other peds and the player (civilians only) */
  private separate(dt: number): void {
    const peds = G.entities.peds;
    const px = this.pos.x, pz = this.pos.z;
    const hx = Math.sin(this.heading), hz = Math.cos(this.heading);
    for (let i = 0; i < peds.length; i++) {
      const p = peds[i];
      if (p === this || p.vehicle || p.state === 'dead' || p.state === 'knocked') continue;
      const dx = px - p.pos.x, dz = pz - p.pos.z;
      const d2 = dx * dx + dz * dz;
      if (d2 > 1 || d2 < 1e-6) continue;
      const d = Math.sqrt(d2);
      const push = (1 - d) * 1.6 * dt;
      const nx = dx / d, nz = dz / d;
      // head-on: step to the right instead of pushing straight back
      if (Math.abs(nx * hx + nz * hz) > 0.8) {
        this.pos.x -= hz * push;
        this.pos.z += hx * push;
      } else {
        this.pos.x += nx * push;
        this.pos.z += nz * push;
      }
    }
    const pl = G.player;
    if (!pl.vehicle) {
      const dx = px - pl.pos.x, dz = pz - pl.pos.z;
      const d2 = dx * dx + dz * dz;
      if (d2 < 1.44 && d2 > 1e-6) {
        const d = Math.sqrt(d2);
        const push = (1.2 - d) * 3.5 * dt;
        this.pos.x += (dx / d) * push;
        this.pos.z += (dz / d) * push;
      }
    }
  }

  // ---- common tail -----------------------------------------------------------------

  private finish(dt: number, t: number): void {
    if (!this.controlled && this.state !== 'knocked' && this.state !== 'dead') this.separate(dt);
    const hit = G.city.resolveCircle(this.pos.x, this.pos.z, RADIUS);
    this.touchedWall = false;
    if (hit) {
      this.pos.x = hit.x;
      this.pos.z = hit.z;
      this.touchedWall = true;
    }
    this.pushOutOfVehicles();
    G.city.clampToBounds(this.pos);
    this.pos.y = 0;
    if (!Number.isFinite(this.pos.x) || !Number.isFinite(this.pos.z) || !Number.isFinite(this.heading)) {
      if (!Number.isFinite(this.pos.x)) this.pos.x = 0;
      if (!Number.isFinite(this.pos.z)) this.pos.z = 0;
      if (!Number.isFinite(this.heading)) this.heading = 0;
      this.speed = 0;
      this.slideX = this.slideZ = 0;
    }
    this.group.position.copy(this.pos);
    this.group.rotation.y = this.heading;

    let a: HumanoidAnim;
    if (this.state === 'dead' || this.state === 'knocked') a = 'dead';
    else if (t < this.hitUntil) a = 'hit';
    else if (t < this.shootUntil) a = 'aim';
    else if (this.speed > 3.6) a = 'run';
    else if (this.speed > 0.15) a = 'walk';
    else a = 'idle';
    this.setAnim(a);
    this.humanoid.update(dt, this.speed);
  }

  private setAnim(a: HumanoidAnim): void {
    if (this.humanoid.anim !== a) this.humanoid.setAnim(a);
  }

  // ================================================================ contract ====

  moveToward(target: THREE.Vector3, dt: number, run: boolean): boolean {
    const dx = target.x - this.pos.x, dz = target.z - this.pos.z;
    const d = Math.hypot(dx, dz);
    if (d < 0.6) {
      this.mvActive = false;
      this.mvSpeed = 0;
      return true;
    }
    const desired = Math.atan2(dx, dz);
    this.heading = dampAngle(this.heading, desired, 10, dt);
    const align = clamp(Math.cos(angleDiff(this.heading, desired)) + 0.3, 0.2, 1);
    const tgt = (run ? CTRL_RUN : CTRL_WALK) * align;
    this.mvSpeed = damp(this.mvSpeed, tgt, 8, dt);
    this.mvActive = true;
    return false;
  }

  faceToward(target: THREE.Vector3): void {
    this.heading = Math.atan2(target.x - this.pos.x, target.z - this.pos.z);
  }

  applyDamage(amount: number, source: DamageSource, fromPos?: THREE.Vector3): void {
    if (this.state === 'dead' || !(amount > 0)) return;
    const t = G.time.t;
    this.hurt(amount, source);
    if ((this.state as PedState) === 'dead') return;
    if (this.vehicle) {
      // shot while seated: civilians want out as soon as the car stops
      if (this.role !== 'cop' && this.releaseOnExit) this.wantsOut = true;
      return;
    }
    if (this.state === 'knocked') return;
    this.hitUntil = t + 0.35;
    if (this.role === 'cop' || this.controlled) return;
    if (fromPos) this.scare(fromPos.x, fromPos.z);
    else if (source === 'player' || source === 'cop') this.scare(G.player.pos.x, G.player.pos.z);
    else this.scare(this.pos.x - Math.sin(this.heading), this.pos.z - Math.cos(this.heading));
  }

  knockDown(dirX: number, dirZ: number, force: number, source: DamageSource): void {
    if (this.state === 'dead' || this.vehicle) return;
    const t = G.time.t;
    let l = Math.hypot(dirX, dirZ);
    if (l < 1e-4) {
      dirX = Math.sin(this.heading);
      dirZ = Math.cos(this.heading);
      l = 1;
    }
    dirX /= l;
    dirZ /= l;
    const x = this.pos.x, z = this.pos.z;
    this.knockFromX = x - dirX * 3;
    this.knockFromZ = z - dirZ * 3;
    this.hurt(force, source);
    if ((this.state as PedState) !== 'dead') {
      this.state = 'knocked';
      this.knockUntil = t + LIE_TIME;
    }
    const slideDist = clamp(force / 40, 1, 2);
    const v0 = slideDist * 4; // exp(-4t) decay integrates to v0/4
    this.slideX = dirX * v0;
    this.slideZ = dirZ * v0;
    this.spin = (G.rng() - 0.5) * 5;
    this.heading = Math.atan2(dirX, dirZ);
    this.speed = 0;
    this.mvActive = false;
    this.mvSpeed = 0;
    G.audio.play('ped_hit', x, z);
    G.particles.emit('blood', x, 0.9, z, 3, dirX, 0.4, dirZ);
  }

  playShoot(): void {
    this.shootUntil = G.time.t + 0.5;
  }

  enterVehicle(v: IVehicle, seat: number): void {
    if (this.vehicle && this.vehicle !== v) this.exitVehicle();
    this.vehicle = v;
    this.seat = seat;
    if (seat === 0) v.driver = this;
    else if (v.passengers.indexOf(this) < 0) v.passengers.push(this);
    if (this.state !== 'dead') this.state = 'seated';
    this.speed = 0;
    this.mvActive = false;
    this.mvSpeed = 0;
    this.slideX = this.slideZ = this.spin = 0;
    this.wantsOut = false;
    this.group.visible = true;
    this.syncSeat(v);
    this.setAnim(seat === 0 ? 'drive' : 'sit');
  }

  exitVehicle(): void {
    const v = this.vehicle;
    if (!v) return;
    if (v.driver === this) v.driver = null;
    const i = v.passengers.indexOf(this);
    if (i >= 0) v.passengers.splice(i, 1);
    this.vehicle = null;
    this.pendingExit = false;
    this.wantsOut = false;
    v.doorPosition(_a);
    this.pos.set(_a.x, 0, _a.z);
    G.city.clampToBounds(this.pos);
    this.humanoid.group.position.y = 0;
    this.heading = v.heading;
    this.speed = 0;
    this.laneIdx = -1;
    if (this.state !== 'dead') {
      if (this.releaseOnExit) {
        this.releaseOnExit = false;
        this.controlled = false;
        this.state = 'idle';
        this.idleUntil = G.time.t;
      } else {
        this.state = this.controlled ? 'controlled' : 'idle';
        this.idleUntil = G.time.t;
      }
    }
    this.group.visible = true;
    this.group.position.copy(this.pos);
    this.group.rotation.y = this.heading;
  }

  scare(fromX: number, fromZ: number): void {
    if (this.state === 'dead' || this.state === 'knocked' || this.vehicle) return;
    if (this.role === 'cop' || this.controlled) return;
    const t = G.time.t;
    this.state = 'flee';
    this.fleeX = fromX;
    this.fleeZ = fromZ;
    this.fleeUntil = Math.max(this.fleeUntil, t + 6 + G.rng() * 4);
    this.fleeBias = 0;
    this.blockedT = 0;
    this.laneIdx = -1;
  }

  dispose(): void {
    this.humanoid.dispose();
    this.group.clear();
  }

  // ================================================================= internals ====

  private hurt(amount: number, source: DamageSource): void {
    this.health -= amount;
    G.events.emit('pedHit', { ped: this, source });
    if (this.health <= 0) this.die(source);
  }

  private die(source: DamageSource): void {
    if (this.state === 'dead') return;
    this.health = 0;
    this.state = 'dead';
    this.deadSince = G.time.t;
    this.speed = 0;
    this.mvActive = false;
    this.mvSpeed = 0;
    this.wantsOut = false;
    if (this.vehicle) this.pendingExit = true; // leave the car on our next update (never mid-iteration)
    const x = this.pos.x, z = this.pos.z;
    G.particles.emit('blood', x, 0.8, z, 14);
    G.events.emit('pedKilled', { ped: this, source });
  }
}
